import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Col, DatePicker, Row, Select, Space, Statistic, Table, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import * as api from '@apis/reservation-success';
import { ClearMarketMetricProduct, ReservationSuccessMetrics } from '@apis/reservation-success';
import { reservationGradeLabel } from '@utils/reservation-success-labels';
import ReservationSuccessPageHeader from '@components/reservation-success/ReservationSuccessPageHeader';

const { Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const PILOT_FROM = '2026-09-01';
const PILOT_TO = '2026-10-31';

const metricTip = (label: string, tip: string) => <Tooltip title={tip}>{label}</Tooltip>;

function formatRate(rate: number | null | undefined): string {
  return rate == null ? '-' : `${rate.toFixed(1)}%`;
}

const PilotReportPage: React.FC = () => {
  const [draft, setDraft] = useState({ from: PILOT_FROM, to: PILOT_TO, dDay: 3 });
  const [query, setQuery] = useState(draft);

  const reportQuery = useQuery({
    queryKey: ['reservation-success-metrics', query.from, query.to, query.dDay],
    queryFn: () => api.getReservationSuccessMetrics(query),
  });
  const report = reportQuery.data?.data;

  return (
    <div style={{ padding: 24 }}>
      <ReservationSuccessPageHeader
        title="성과 지표"
        description="수업일 기준으로 기간을 바꾸면 같은 정의로 다시 집계합니다. 즉시대응 판정은 선택한 D-day의 마지막 등급이 즉시대응인 회차이고, 실제 취소는 그 회차 좌석이 모두 전액환불된 경우입니다."
      />
      {draft.from === PILOT_FROM && draft.to === PILOT_TO && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          기본 기간 {PILOT_FROM} ~ {PILOT_TO}은 파일럿 기간입니다.
        </Text>
      )}

      <Space wrap style={{ marginBottom: 16 }}>
        <RangePicker
          value={[dayjs(draft.from), dayjs(draft.to)]}
          onChange={(dates) => {
            if (!dates?.[0] || !dates?.[1]) return;
            setDraft((prev) => ({ ...prev, from: dates[0]!.format('YYYY-MM-DD'), to: dates[1]!.format('YYYY-MM-DD') }));
          }}
        />
        <Select
          style={{ width: 180 }}
          value={draft.dDay}
          onChange={(dDay) => setDraft((prev) => ({ ...prev, dDay }))}
          options={[
            { value: 3, label: 'D-3 수업 3일 전' },
            { value: 2, label: 'D-2 수업 2일 전' },
            { value: 1, label: 'D-1 수업 전날' },
          ]}
        />
        <Button
          type="primary"
          loading={reportQuery.isFetching}
          onClick={() => {
            if (query.from === draft.from && query.to === draft.to && query.dDay === draft.dDay) {
              reportQuery.refetch();
              return;
            }
            setQuery(draft);
          }}>
          조회
        </Button>
      </Space>

      {reportQuery.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="집계에 실패했습니다"
          description={
            (reportQuery.error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            '기간은 93일 이하로 선택해 주세요. 잠시 후 다시 조회할 수 있습니다.'
          }
        />
      )}

      {report && <ReportBody report={report} loading={reportQuery.isFetching} />}
    </div>
  );
};

const ReportBody: React.FC<{ report: ReservationSuccessMetrics; loading: boolean }> = ({ report, loading }) => {
  const columns: ColumnsType<ClearMarketMetricProduct> = [
    { title: '상품', dataIndex: 'productName', key: 'productName' },
    { title: '파트너', dataIndex: 'sellerName', key: 'sellerName', width: 160 },
    { title: '운영 회차', dataIndex: 'sessions', key: 'sessions', width: 100, align: 'right' },
    { title: '판매 회차', dataIndex: 'soldSessions', key: 'soldSessions', width: 100, align: 'right' },
    {
      title: '좌석',
      key: 'seats',
      width: 100,
      align: 'right',
      render: (_, row) => `${row.purchased}/${row.capacity}`,
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ display: 'flex' }}>
      <Text type="secondary">
        {report.from} ~ {report.to}, D-{report.dDay}. 같은 조건은 10분간 저장된 결과를 보여 줍니다.
      </Text>
      <Row gutter={[16, 16]}>
        <Col xs={12} md={8} lg={4}>
          <Card loading={loading}>
            <Statistic
              title={metricTip('감지 정확도', '즉시대응으로 본 회차 중 실제로 전원환불된 비율입니다.')}
              value={formatRate(report.backtest.accuracy)}
            />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card loading={loading}>
            <Statistic
              title={metricTip('오탐', '즉시대응으로 봤으나 전원환불이 아닌 회차 비율입니다.')}
              value={formatRate(report.backtest.falsePositiveRate)}
            />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card loading={loading}>
            <Statistic
              title={metricTip('미탐', '즉시대응이 아니었으나 전원환불된 회차 비율입니다.')}
              value={formatRate(report.backtest.falseNegativeRate)}
            />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card loading={loading}>
            <Statistic title="운영 상품" value={report.clearMarket.products} suffix="개" />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card loading={loading}>
            <Statistic title="클리어 전환율" value={formatRate(report.clearMarket.conversion.rate)} />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card loading={loading}>
            <Statistic title="추천 재구매" value={formatRate(report.recommendation.rate)} />
          </Card>
        </Col>
      </Row>

      <Card title="환불률" loading={loading}>
        <Text>
          {report.refund.before.from} ~ {report.refund.before.to}{' '}
          <Text strong>{formatRate(report.refund.before.rate)}</Text>
          {'  '}({report.refund.before.refundedAll}/{report.refund.before.paid}){' → '}
          {report.refund.after.from} ~ {report.refund.after.to}{' '}
          <Text strong>{formatRate(report.refund.after.rate)}</Text>
          {'  '}({report.refund.after.refundedAll}/{report.refund.after.paid})
        </Text>
        <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 8 }}>
          이 기간에 취소되지 않은 클리어마켓 상품의 결제완료 주문 중 전액환불 비율입니다. 수업일 기준입니다.
        </Paragraph>
      </Card>

      <Card title="백테스트" loading={loading}>
        <Table
          size="small"
          pagination={false}
          rowKey="label"
          dataSource={[
            {
              label: '전원환불',
              predicted: report.backtest.truePositive,
              notPredicted: report.backtest.falseNegative,
            },
            {
              label: '전원환불 아님',
              predicted: report.backtest.falsePositive,
              notPredicted: report.backtest.trueNegative,
            },
          ]}
          columns={[
            { title: '', dataIndex: 'label', key: 'label' },
            { title: '즉시대응으로 봄', dataIndex: 'predicted', key: 'predicted', align: 'right' },
            { title: '즉시대응 아님', dataIndex: 'notPredicted', key: 'notPredicted', align: 'right' },
          ]}
        />
        <Paragraph style={{ marginTop: 12, marginBottom: 0 }}>
          판정 회차 {report.backtest.sessions.toLocaleString()}개. 좌석이 없어 제외한 회차{' '}
          {report.backtest.unlabeled.toLocaleString()}개.
        </Paragraph>
        <Table
          style={{ marginTop: 12 }}
          size="small"
          pagination={false}
          rowKey="grade"
          dataSource={report.backtest.grades}
          columns={[
            {
              title: '등급',
              dataIndex: 'grade',
              key: 'grade',
              render: (grade: string) => reservationGradeLabel(grade),
            },
            { title: '회차', dataIndex: 'sessions', key: 'sessions', align: 'right' },
            { title: '전원환불', dataIndex: 'fullRefundSessions', key: 'fullRefundSessions', align: 'right' },
            { title: '비율', dataIndex: 'rate', key: 'rate', align: 'right', render: formatRate },
          ]}
        />
      </Card>

      <Card
        title={`클리어마켓 운영 상품 ${report.clearMarket.products}개 · 파트너 ${report.clearMarket.partners}곳`}
        loading={loading}>
        <Paragraph>
          등록 {report.clearMarket.registeredSessions}회차 중 파트너 취소 {report.clearMarket.cancelledSessions}회차.
          전환율은 수업일이 지난 운영 회차 {report.clearMarket.conversion.denominator}개 중 판매{' '}
          {report.clearMarket.conversion.numerator}개. 좌석 충원 {report.clearMarket.seatFill.numerator}/
          {report.clearMarket.seatFill.denominator} ({formatRate(report.clearMarket.seatFill.rate)}
          ).
        </Paragraph>
        <Paragraph type="secondary">
          추천 클릭 {report.recommendation.clicks}건 중 14일 안에 그 상품을 환불 없이 결제한 경우{' '}
          {report.recommendation.keptPurchases}건.
        </Paragraph>
        <Table
          size="small"
          rowKey="productId"
          pagination={false}
          columns={columns}
          dataSource={report.clearMarket.rows}
        />
      </Card>
    </Space>
  );
};

export default PilotReportPage;
